import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Icon from "@/components/Icon";
import EmberOrb from "@/components/EmberOrb";
import { colors, spacing, radius, fonts, type } from "@/theme";
import { getStoredUserId, fetchUser, sendVoice, sendText, fetchTts, fetchOpener, summarizeCall } from "@/api";
import { logError } from "@/lib/log";
import { playBase64Mp3, stopAudio } from "@/lib/audioPlayer";
import { useWebRecorder } from "@/hooks/useWebRecorder";

const DISCLOSURE_KEY = "cozyvoice_disclosure_seen";
const BARS = [0, 1, 2, 3, 4, 5, 6];

function Waveform({ active, color }) {
  return (
    <div style={{ display: "flex", alignItems: "center", height: 72, gap: 8, marginBottom: spacing.lg }} data-testid="voice-waveform">
      {BARS.map((i) => (
        <div key={i} style={{
          width: 10, height: 60, borderRadius: 5, background: color, transformOrigin: "center",
          transform: active ? undefined : "scaleY(0.22)",
          animation: active ? `emberBar ${0.64 + i * 0.08}s ease-in-out ${i * 0.05}s infinite` : "none",
        }} />
      ))}
    </div>
  );
}

export default function Conversation() {
  const navigate = useNavigate();
  const location = useLocation();
  const openerParam = location.state?.opener;
  const recorder = useWebRecorder();

  const [userId, setUserId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState("idle"); // idle|listening|thinking|speaking
  const [showType, setShowType] = useState(false);
  const [showWords, setShowWords] = useState(false);
  const [draft, setDraft] = useState("");
  const [showDisclosure, setShowDisclosure] = useState(false);
  const scrollRef = useRef(null);
  const speakTimer = useRef(null);

  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");

  const speakFor = (text) => {
    if (speakTimer.current) clearTimeout(speakTimer.current);
    setStatus("speaking");
    const words = text.split(/\s+/).length;
    const ms = Math.min(20000, Math.max(2500, words * 380));
    speakTimer.current = setTimeout(() => setStatus("idle"), ms);
  };

  useEffect(() => {
    if (!localStorage.getItem(DISCLOSURE_KEY)) setShowDisclosure(true);
  }, []);

  const dismissDisclosure = () => {
    localStorage.setItem(DISCLOSURE_KEY, "1");
    setShowDisclosure(false);
  };

  useEffect(() => {
    (async () => {
      const id = getStoredUserId();
      if (!id) {
        navigate("/onboarding", { replace: true });
        return;
      }
      setUserId(id);
      let uname = "friend";
      let spark = null;
      try {
        const data = await fetchUser(id);
        uname = data.user.first_name;
        if (!openerParam && !data.is_returning && data.user.about) spark = await fetchOpener(id);
      } catch (e) { logError("Conversation load", e); }
      const opener = openerParam || spark || `Hello ${uname}. It's so good to hear from you. How are you feeling today?`;
      setMessages([{ id: "opener", role: "assistant", text: opener }]);
      const audio = await fetchTts(opener);
      if (audio) {
        playBase64Mp3(audio);
        speakFor(opener);
      }
    })();
    return () => {
      stopAudio();
      if (speakTimer.current) clearTimeout(speakTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 80);
  }, [messages, showWords]);

  const handleReply = async (promise) => {
    setStatus("thinking");
    try {
      const res = await promise;
      setMessages((prev) => {
        const next = [...prev];
        if (res.transcript) next.push({ id: `u-${Date.now()}`, role: "user", text: res.transcript });
        next.push({ id: `a-${Date.now()}`, role: "assistant", text: res.reply });
        return next;
      });
      if (res.reply_audio) {
        playBase64Mp3(res.reply_audio);
        speakFor(res.reply);
      } else {
        setStatus("idle");
      }
    } catch {
      setMessages((prev) => [...prev, { id: `err-${Date.now()}`, role: "assistant", text: "I'm sorry, I didn't quite catch that. Could you try again?" }]);
      setStatus("idle");
    }
  };

  const startListening = async () => {
    if (!userId) return;
    stopAudio();
    if (speakTimer.current) clearTimeout(speakTimer.current);
    const granted = await recorder.start();
    if (granted) setStatus("listening");
  };

  const stopListening = async () => {
    const blob = await recorder.stop();
    if (blob && userId) await handleReply(sendVoice(userId, blob));
    else setStatus("idle");
  };

  const onMicPress = () => {
    if (status === "thinking") return;
    if (status === "listening") stopListening();
    else startListening();
  };

  const sendDraft = async () => {
    const text = draft.trim();
    if (!text || !userId || status === "thinking") return;
    setDraft("");
    setMessages((prev) => [...prev, { id: `u-${Date.now()}`, role: "user", text }]);
    await handleReply(sendText(userId, text).then((r) => ({ ...r, transcript: "" })));
  };

  const endCall = () => {
    stopAudio();
    if (userId) summarizeCall(userId);
    navigate("/home", { replace: true });
  };

  const statusLabel = status === "listening" ? "I'm listening…" : status === "thinking" ? "Thinking…" : status === "speaking" ? "Speaking…" : "Tap the button to talk";
  const active = status !== "idle";

  return (
    <div style={{ background: colors.surface, minHeight: "100vh" }}>
      <div style={{ maxWidth: 560, margin: "0 auto", minHeight: "100vh", display: "flex", flexDirection: "column", background: colors.surface }} data-testid="conversation-screen">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: `${spacing.sm}px ${spacing.md}px` }}>
          <div style={{ width: 48 }} />
          <span data-testid="companion-name" style={{ ...fonts.displaySemiBold, fontSize: type.xl, color: colors.onSurface }}>Ember</span>
          <button data-testid="toggle-words-button" onClick={() => setShowWords((s) => !s)} style={{ width: 48, height: 48, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name={showWords ? "eye-off" : "eye"} size={24} color={colors.brand} />
          </button>
        </div>

        <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          {showWords ? (
            <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: `${spacing.md}px ${spacing.lg}px`, display: "flex", flexDirection: "column", gap: spacing.md }}>
              {messages.map((m) => (
                <div key={m.id} data-testid={`bubble-${m.role}`} style={{
                  maxWidth: "88%", borderRadius: radius.lg, padding: `${spacing.md}px ${spacing.lg}px`,
                  ...(m.role === "assistant"
                    ? { background: colors.surfaceSecondary, alignSelf: "flex-start", borderTopLeftRadius: radius.sm }
                    : { background: colors.brandPrimary, alignSelf: "flex-end", borderTopRightRadius: radius.sm }),
                }}>
                  <span style={{ ...fonts.textSemiBold, fontSize: type.lg, lineHeight: 1.4, color: m.role === "assistant" ? colors.onSurface : colors.onBrandPrimary }}>{m.text}</span>
                </div>
              ))}
              {status === "thinking" && (
                <div data-testid="typing-indicator" style={{ maxWidth: "88%", borderRadius: radius.lg, padding: `${spacing.md}px ${spacing.lg}px`, background: colors.surfaceSecondary, alignSelf: "flex-start" }}>
                  <span style={{ ...fonts.textSemiBold, color: colors.onSurface }}>· · ·</span>
                </div>
              )}
            </div>
          ) : (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: `0 ${spacing.xl}px` }}>
              <div className="ember-orb-in" style={{ borderRadius: "50%", border: `4px solid ${active ? colors.brandSecondary : colors.brandTertiary}`, padding: 6, marginBottom: spacing.xl }}>
                <EmberOrb size={176} active={active} />
              </div>
              <Waveform active={active} color={status === "listening" ? colors.error : colors.brand} />
              <div data-testid="status-label" style={{ ...fonts.textBold, fontSize: type.xl, color: colors.onSurface, marginBottom: spacing.md }}>{statusLabel}</div>
              {lastAssistant && (
                <div data-testid="last-caption" style={{ ...fonts.text, fontSize: type.lg, color: colors.muted, textAlign: "center", lineHeight: 1.5, maxWidth: 440 }}>
                  {status === "listening" ? "" : lastAssistant.text}
                </div>
              )}
            </div>
          )}

          {recorder.permissionBlocked && (
            <div data-testid="perm-banner" style={{ margin: `0 ${spacing.lg}px ${spacing.md}px`, background: colors.brandTertiary, borderRadius: radius.md, padding: spacing.lg }}>
              <div style={{ ...fonts.textSemiBold, fontSize: type.base, color: colors.onSurface }}>
                Microphone access is off. Allow it in your browser to talk, or type instead.
              </div>
            </div>
          )}

          <div style={{ padding: `${spacing.md}px ${spacing.lg}px ${spacing.xl}px`, borderTop: `1px solid ${colors.divider}`, display: "flex", flexDirection: "column", alignItems: "center", gap: spacing.md }}>
            {showType ? (
              <div style={{ display: "flex", alignItems: "flex-end", gap: spacing.md, width: "100%" }}>
                <textarea data-testid="conversation-text-input" value={draft} onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendDraft(); } }}
                  placeholder="Type your message…"
                  style={{ flex: 1, minHeight: 60, maxHeight: 140, background: colors.surfaceSecondary, borderRadius: radius.lg, border: `2px solid ${colors.border}`, padding: `${spacing.md}px ${spacing.lg}px`, ...fonts.textSemiBold, fontSize: type.lg, color: colors.onSurface }} />
                <button data-testid="conversation-send-button" onClick={sendDraft} className="ember-press" style={{ width: 60, height: 60, borderRadius: 30, background: colors.brandPrimary, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon name="send" size={26} color={colors.onBrandPrimary} />
                </button>
              </div>
            ) : (
              <button data-testid="mic-button" onClick={onMicPress} className="ember-press"
                style={{ width: 96, height: 96, borderRadius: 48, background: status === "listening" ? colors.error : colors.brandPrimary, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name={status === "listening" ? "square" : "mic"} size={44} color={colors.onBrandPrimary} />
              </button>
            )}

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: spacing.md }}>
              <button data-testid="toggle-type-mode" onClick={() => setShowType((s) => !s)} className="ember-press"
                style={{ flex: 1, minHeight: 72, display: "flex", alignItems: "center", justifyContent: "center", gap: spacing.sm, background: colors.surfaceSecondary, borderRadius: radius.lg, border: `2px solid ${colors.border}` }}>
                <Icon name={showType ? "mic" : "edit-3"} size={20} color={colors.brand} />
                <span style={{ ...fonts.textBold, fontSize: type.base, color: colors.onSurface }}>{showType ? "Use voice" : "Type instead"}</span>
              </button>
              <button data-testid="end-call-button" onClick={endCall} className="ember-press"
                style={{ flex: 1, minHeight: 72, display: "flex", alignItems: "center", justifyContent: "center", gap: spacing.sm, background: colors.error, borderRadius: radius.lg }}>
                <Icon name="phone-off" size={22} color={colors.onError} />
                <span style={{ ...fonts.textExtraBold, fontSize: type.lg, color: colors.onError }}>End call</span>
              </button>
            </div>

            <p data-testid="ai-disclaimer" style={{ ...fonts.text, fontSize: type.base, color: colors.muted, textAlign: "center", lineHeight: 1.4, margin: 0 }}>
              I&rsquo;m an AI companion — a caring voice, not a real person, and not a source of medical, legal, or financial advice.
            </p>
          </div>
        </div>
      </div>

      {showDisclosure && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(22,48,43,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: spacing.xl, zIndex: 50 }}>
          <div data-testid="disclosure-modal" style={{ width: "100%", maxWidth: 420, background: colors.surface, borderRadius: radius.lg, padding: spacing.xl, display: "flex", flexDirection: "column", alignItems: "center", gap: spacing.md }}>
            <div style={{ width: 72, height: 72, borderRadius: 36, background: colors.brandPrimary, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon name="heart" size={32} color={colors.onBrandPrimary} />
            </div>
            <div style={{ ...fonts.displayBold, fontSize: type["2xl"], color: colors.onSurface }}>A warm hello</div>
            <div style={{ ...fonts.text, fontSize: type.lg, color: colors.onSurface, textAlign: "center", lineHeight: 1.5 }}>
              Ember is here to keep you company. It&rsquo;s an AI — not a real person — but it genuinely cares about you and has all the time in the world to listen.
            </div>
            <button data-testid="disclosure-ok-button" onClick={dismissDisclosure} className="ember-press"
              style={{ minHeight: 76, width: "100%", borderRadius: radius.lg, background: colors.brandPrimary, ...fonts.textExtraBold, fontSize: type.xl, color: colors.onBrandPrimary }}>
              I understand
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
